import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

type Props = { children: ReactNode; fallback?: ReactNode; onReset?: () => void };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div role="alert" className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-danger/10 text-danger">
          <AlertTriangle className="size-7" />
        </div>
        <div className="space-y-1">
          <h2 className="font-display text-xl font-semibold">Something went wrong</h2>
          <p className="text-sm text-fg-muted">
            This page hit an unexpected error. Reloading usually fixes it; if it keeps happening, let us know.
          </p>
          {import.meta.env.DEV && <pre className="mt-3 max-w-full overflow-auto rounded-md bg-surface-2 p-3 text-left text-xs text-danger">{this.state.error.message}</pre>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={this.reset}>
            Try again
          </Button>
          <Button onClick={() => window.location.reload()}>
            <RotateCcw />
            Reload
          </Button>
        </div>
      </div>
    );
  }
}
